import React from "react";
import { GUIDE_SECTIONS } from "./spotOrderingHelpContent";
import HelpSystemDialogShell from "./HelpSystemDialogShell";

function StepMedia({ image }) {
	if (!image) {
		return null;
	}

	if (image.src) {
		return (
			<div className="mt-5 overflow-hidden rounded-3xl border border-slate-200 bg-white p-3">
				<div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
					<img
						src={image.src}
						alt={image.alt || image.title || "Guide screenshot"}
						loading="lazy"
						className="max-h-[440px] w-full object-contain"
					/>
				</div>
				<div className="px-1 pb-1 pt-3">
					<p className="text-sm font-semibold text-slate-900">{image.title}</p>
					{image.caption ? (
						<p className="mt-1 text-xs leading-5 text-slate-500">{image.caption}</p>
					) : null}
				</div>
			</div>
		);
	}

	return (
		<div className="mt-5 rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4">
			<div className="flex aspect-[16/10] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 text-center">
				<div>
					<p className="text-sm font-semibold text-slate-700">Screenshot Placeholder</p>
					<p className="mt-2 text-xs leading-5 text-slate-500">{image.title}</p>
				</div>
			</div>
			<p className="mt-3 text-sm font-semibold text-slate-900">{image.title}</p>
			<p className="mt-1 text-xs leading-5 text-slate-500">{image.caption}</p>
		</div>
	);
}

function GuideSectionCard({ section }) {
	return (
		<section
			data-help-section={section.id}
			className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:p-6"
		>
			<div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-5">
				<div className="min-w-0">
					<h3 className="mt-2 text-2xl font-bold text-slate-900">{section.title}</h3>
					<p className="mt-3 max-w-4xl text-sm leading-6 text-slate-600">{section.summary}</p>
				</div>
			</div>

			{section.highlights?.length > 0 ? (
				<div className="mt-5 grid gap-3 xl:grid-cols-3">
					{section.highlights.map((highlight) => (
						<div
							key={highlight}
							className="rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-sm leading-6 text-slate-700"
						>
							{highlight}
						</div>
					))}
				</div>
			) : null}

			{section.steps?.length > 0 ? (
				<div className="mt-6 flex flex-col gap-4">
					{section.steps.map((step, stepIndex) => (
						<div
							key={`${section.id}-${step.title}`}
							className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4 md:p-5"
						>
							<div className="min-w-0">
								<div className="flex items-start gap-3">
									<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-700 text-sm font-bold text-white">
										{stepIndex + 1}
									</div>
									<div className="min-w-0 flex-1">
										<h4 className="text-lg font-semibold text-slate-900">{step.title}</h4>
										<div className="mt-3 flex flex-col gap-3 text-sm leading-6 text-slate-600">
											{step.description?.map((paragraph) => (
												<p key={paragraph}>{paragraph}</p>
											))}
										</div>
										{step.note ? (
											<div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
												{step.note}
											</div>
										) : null}
										<StepMedia image={step.image} />
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			) : null}

			{section.notes?.length > 0 ? (
				<div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-4 md:p-5">
					<p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
						Keep In Mind
					</p>
					<div className="mt-3 flex flex-col gap-3">
						{section.notes.map((note) => (
							<div
								key={note}
								className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-600"
							>
								{note}
							</div>
						))}
					</div>
				</div>
			) : null}
		</section>
	);
}

export default function GuideDialog({ open, onClose }) {
	return (
		<HelpSystemDialogShell
			open={open}
			onClose={onClose}
			title="Guide for Ordering SPOT Surveys"
			subtitle="This guide walks through the current SPOT survey ordering workflow. It covers managing single courses, working in bulk, creating instanced classes, and understanding the recent interface changes."
			sections={GUIDE_SECTIONS}
		>
			{GUIDE_SECTIONS.map((section) => (
				<GuideSectionCard key={section.id} section={section} />
			))}
		</HelpSystemDialogShell>
	);
}
